/**
 * Fuzzy Field Matching Utility
 *
 * Provides fuzzy string matching for mapping fields between systems
 * (e.g., CSV columns to Monday.com board columns)
 */

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0]![j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i]![j] = matrix[i - 1]![j - 1]!;
      } else {
        matrix[i]![j] = Math.min(
          matrix[i - 1]![j - 1]! + 1, // substitution
          matrix[i]![j - 1]! + 1,     // insertion
          matrix[i - 1]![j]! + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length]![a.length]!;
}

/**
 * Normalize a string for comparison
 * - Lowercase
 * - Remove special characters
 * - Collapse whitespace
 * - Remove common suffixes/prefixes
 */
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')  // Replace special chars with space
    .replace(/\s+/g, ' ')           // Collapse whitespace
    .trim();
}

/**
 * Split string into tokens for matching
 */
function tokenize(str: string): string[] {
  return normalizeString(str)
    .split(' ')
    .filter(Boolean);
}

/**
 * Check if tokens overlap significantly
 */
function tokenOverlapScore(a: string, b: string): number {
  const tokensA = tokenize(a);
  const tokensB = tokenize(b);

  if (tokensA.length === 0 || tokensB.length === 0) return 0;

  let matchingTokens = 0;
  for (const tokenA of tokensA) {
    for (const tokenB of tokensB) {
      // Check for exact match or one contains the other
      if (tokenA === tokenB || tokenA.includes(tokenB) || tokenB.includes(tokenA)) {
        matchingTokens++;
        break;
      }
    }
  }

  // Return ratio of matching tokens to total unique tokens
  const totalUniqueTokens = new Set([...tokensA, ...tokensB]).size;
  return matchingTokens / totalUniqueTokens;
}

/**
 * Field aliases for common variations
 */
const FIELD_ALIASES: Record<string, string[]> = {
  // Names
  'first name': ['firstname', 'first', 'given name', 'fname', 'f name'],
  'last name': ['lastname', 'last', 'surname', 'family name', 'lname', 'l name'],
  'full name': ['name', 'contact name', 'fullname', 'contact', 'person'],

  // Contact info
  'email': ['e-mail', 'mail', 'email address', 'contact email', 'work email', 'email1'],
  'phone': ['telephone', 'tel', 'mobile', 'cell', 'work phone', 'direct phone', 'phone number', 'corporate phone', 'phone1'],

  // Job info
  'title': ['job title', 'position', 'role', 'designation', 'job role'],
  'company': ['company name', 'organization', 'org', 'business', 'account', 'firm', 'employer', 'account name'],
  'seniority': ['level', 'seniority level', 'job level', 'management level'],
  'department': ['departments', 'dept', 'division', 'team', 'function'],

  // Location
  'city': ['town', 'municipality'],
  'state': ['province', 'region', 'territory'],
  'country': ['nation', 'country/region'],

  // Social/Web
  'linkedin': ['linkedin url', 'linkedin profile', 'person linkedin', 'contact linkedin'],
  'company linkedin': ['org linkedin', 'organization linkedin', 'company linkedin url'],
  'website': ['web', 'url', 'company website', 'domain', 'site', 'homepage'],

  // Business info
  'industry': ['sector', 'vertical', 'market', 'business type'],
  'employees': ['employee count', '# employees', 'company size', 'headcount', 'num employees', 'number of employees', 'size'],
  'revenue': ['annual revenue', 'yearly revenue', 'arr', 'total revenue'],
  'technologies': ['tech stack', 'tools', 'software', 'tech'],

  // CRM fields
  'owner': ['contact owner', 'account owner', 'assigned to', 'rep', 'sales rep', 'salesperson', 'assigned'],
  'source': ['lead source', 'origin', 'channel', 'referral', 'how did'],
  'stage': ['status', 'lead stage', 'phase', 'pipeline stage'],
  'value': ['deal value', 'amount', 'deal amount', 'opportunity value', 'price'],
  'notes': ['note', 'comments', 'description', 'remarks'],
};

/**
 * Build a reverse lookup from aliases to canonical field names
 */
function buildAliasLookup(): Map<string, string> {
  const lookup = new Map<string, string>();

  for (const [canonical, aliases] of Object.entries(FIELD_ALIASES)) {
    lookup.set(normalizeString(canonical), canonical);
    for (const alias of aliases) {
      lookup.set(normalizeString(alias), canonical);
    }
  }

  return lookup;
}

const aliasLookup = buildAliasLookup();

/**
 * Get the canonical field name for a string using alias lookup
 */
function getCanonicalName(str: string): string | null {
  const normalized = normalizeString(str);
  return aliasLookup.get(normalized) || null;
}

export interface FuzzyMatchResult {
  sourceField: string;
  targetField: string;
  targetId: string;
  score: number;
  matchType: 'exact' | 'alias' | 'fuzzy' | 'token';
}

export interface TargetColumn {
  id: string;
  title: string;
}

/**
 * Calculate match score between two field names
 * Returns a score from 0 to 1, where 1 is a perfect match
 */
export function calculateMatchScore(source: string, target: string): { score: number; matchType: 'exact' | 'alias' | 'fuzzy' | 'token' } {
  const normalizedSource = normalizeString(source);
  const normalizedTarget = normalizeString(target);

  // Exact match after normalization
  if (normalizedSource === normalizedTarget) {
    return { score: 1.0, matchType: 'exact' };
  }

  // Check if both resolve to the same canonical name (alias match)
  const sourceCanonical = getCanonicalName(source);
  const targetCanonical = getCanonicalName(target);

  if (sourceCanonical && targetCanonical && sourceCanonical === targetCanonical) {
    return { score: 0.95, matchType: 'alias' };
  }

  // Check if source is an alias of target's canonical form
  if (targetCanonical && getCanonicalName(source) === targetCanonical) {
    return { score: 0.9, matchType: 'alias' };
  }

  // Token overlap scoring
  const tokenScore = tokenOverlapScore(source, target);
  if (tokenScore >= 0.5) {
    return { score: 0.7 + (tokenScore * 0.2), matchType: 'token' };
  }

  // Levenshtein-based fuzzy matching
  const maxLen = Math.max(normalizedSource.length, normalizedTarget.length);
  if (maxLen === 0) return { score: 0, matchType: 'fuzzy' };

  const distance = levenshteinDistance(normalizedSource, normalizedTarget);
  const similarity = 1 - (distance / maxLen);

  // Only consider it a match if similarity is above threshold
  if (similarity >= 0.6) {
    return { score: similarity * 0.8, matchType: 'fuzzy' };
  }

  return { score: 0, matchType: 'fuzzy' };
}

/**
 * Find the best matching target column for a source field
 */
export function findBestMatch(
  sourceField: string,
  targetColumns: TargetColumn[],
  minScore = 0.5
): FuzzyMatchResult | null {
  let bestMatch: FuzzyMatchResult | null = null;

  for (const target of targetColumns) {
    const { score, matchType } = calculateMatchScore(sourceField, target.title);

    if (score >= minScore && (!bestMatch || score > bestMatch.score)) {
      bestMatch = {
        sourceField,
        targetField: target.title,
        targetId: target.id,
        score,
        matchType,
      };
    }
  }

  return bestMatch;
}

/**
 * Match multiple source fields to target columns
 * Returns a map from source field to target column ID
 */
export function matchFields(
  sourceFields: string[],
  targetColumns: TargetColumn[],
  minScore = 0.5
): Map<string, { id: string; title: string; score: number; matchType: string }> {
  const matches = new Map<string, { id: string; title: string; score: number; matchType: string }>();
  const usedTargetIds = new Set<string>();

  // Sort source fields by specificity (longer = more specific)
  const sortedSources = [...sourceFields].sort((a, b) => b.length - a.length);

  for (const source of sortedSources) {
    let bestMatch: FuzzyMatchResult | null = null;

    for (const target of targetColumns) {
      // Skip already used targets
      if (usedTargetIds.has(target.id)) continue;

      const { score, matchType } = calculateMatchScore(source, target.title);

      if (score >= minScore && (!bestMatch || score > bestMatch.score)) {
        bestMatch = {
          sourceField: source,
          targetField: target.title,
          targetId: target.id,
          score,
          matchType,
        };
      }
    }

    if (bestMatch) {
      matches.set(source, {
        id: bestMatch.targetId,
        title: bestMatch.targetField,
        score: bestMatch.score,
        matchType: bestMatch.matchType,
      });
      usedTargetIds.add(bestMatch.targetId);
    }
  }

  return matches;
}

/**
 * Debug helper to show all potential matches for a source field
 */
export function debugMatches(
  sourceField: string,
  targetColumns: TargetColumn[]
): Array<{ target: string; id: string; score: number; matchType: string }> {
  return targetColumns
    .map((target) => {
      const { score, matchType } = calculateMatchScore(sourceField, target.title);
      return { target: target.title, id: target.id, score, matchType };
    })
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score);
}
